
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { PlusCircle, Users, FileText, Clock, Video } from 'lucide-react';
import ProjectCard from '@/components/collaboration/ProjectCard';
import SessionCard from '@/components/collaboration/SessionCard';
import FileCard from '@/components/collaboration/FileCard';
import { mockCollaborationProjects, mockCollaborationSessions, mockProjectFiles } from '@/types/collaboration';
import { useToast } from '@/hooks/use-toast';

const ProjectDetails = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [activeTab, setActiveTab] = useState('overview');
  const { toast } = useToast();
  
  console.log("Project ID:", projectId);
  console.log("Available projects:", mockCollaborationProjects);
  
  // Find project from mock data
  const project = mockCollaborationProjects.find(p => p.id === projectId);
  
  // Find sessions for this project
  const projectSessions = mockCollaborationSessions.filter(s => s.projectId === projectId);
  
  // Find files for this project
  const projectFiles = mockProjectFiles.filter(f => f.projectId === projectId);
  
  console.log("Found project:", project);
  console.log("Found sessions:", projectSessions);
  console.log("Session IDs:", projectSessions.map(s => s.id).join(', '));
  
  if (!project) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <h1 className="text-2xl font-bold mb-4">Project not found</h1>
          <p>The project you're looking for does not exist or has been removed.</p>
          <Button asChild className="mt-4">
            <Link to="/collaboration">Back to Collaboration</Link>
          </Button>
        </div>
      </Layout>
    );
  }
  
  // Get the first session ID
  const firstSessionId = projectSessions.length > 0 ? projectSessions[0].id : null;
  
  const handleTestLiveSession = () => {
    if (firstSessionId) {
      toast({
        title: "Joining test session",
        description: `Joining session with ID: ${firstSessionId}`
      });
    }
  };
  
  return (
    <Layout>
      <div className="container mx-auto py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">{project.name}</h1>
            <p className="text-muted-foreground">{project.description}</p>
          </div>
          <div className="flex gap-2">
            {firstSessionId ? (
              <Button 
                variant="outline" 
                size="sm" 
                asChild
                onClick={handleTestLiveSession}
              >
                <Link to={`/collaboration/sessions/${firstSessionId}`} className="flex items-center">
                  <Video className="mr-2 h-4 w-4" />
                  Test Live Session ({firstSessionId})
                </Link>
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled>
                <Video className="mr-2 h-4 w-4" />
                No Sessions Available
              </Button>
            )}
            <Button variant="default">
              <PlusCircle className="mr-2 h-4 w-4" />
              New Session
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="sessions">Sessions</TabsTrigger>
            <TabsTrigger value="files">Files</TabsTrigger>
            <TabsTrigger value="team">Team</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ProjectCard project={project} />
              <div className="md:col-span-2 lg:col-span-2">
                <h3 className="text-xl font-bold mb-2">Project Details</h3>
                <p>
                  <strong>Created By:</strong> {project.createdBy}
                </p>
                <p>
                  <strong>Created At:</strong> {project.createdAt.toLocaleDateString()}
                </p>
                <p>
                  <strong>Last Updated:</strong> {project.lastUpdated.toLocaleDateString()}
                </p>
                <p>
                  <strong>Status:</strong> {project.status}
                </p>
                <p>
                  <strong>Type:</strong> {project.type}
                </p>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="sessions">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projectSessions.map(session => (
                <SessionCard key={session.id} session={session} />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="files">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projectFiles.map(file => (
                <FileCard key={file.id} file={file} />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="team">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {project.collaborators.map(collaborator => (
                <div key={collaborator.id} className="bg-card rounded-lg p-4">
                  <h3 className="text-lg font-bold">{collaborator.name}</h3>
                  <p>
                    <strong>Role:</strong> {collaborator.role}
                  </p>
                  <p>
                    <strong>Status:</strong> {collaborator.status}
                  </p>
                  {collaborator.joinedAt && (
                    <p>
                      <strong>Joined At:</strong> {collaborator.joinedAt.toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
        
      </div>
    </Layout>
  );
};

export default ProjectDetails;
